import { Link, useLocation } from "react-router-dom";
import { FiLogOut, FiMenu } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { getBreadcrumbs } from "../../data/routeBreadcrumbs";
import { logoutUser, selectAuth } from "../../redux/auth/authSlice";

function AppTopbar({ sidebarOpen, onSidebarToggle }) {
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector(selectAuth);
  const breadcrumbs = getBreadcrumbs(location.pathname);

  const initials =
    user?.name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() ||
    user?.email?.[0]?.toUpperCase() ||
    "?";

  return (
    <header className="sticky top-0 z-30 shrink-0 border-b border-base-200 bg-base-100/90 shadow-sm backdrop-blur-md">
      <div className="navbar min-h-14 gap-2 px-3 sm:px-5 lg:min-h-[3.75rem]">
        <div className="navbar-start min-w-0 flex-1 gap-2 lg:gap-3">
          <button
            type="button"
            className="btn btn-square btn-ghost drawer-button shrink-0 border-0 text-base-content hover:bg-transparent hover:!bg-transparent focus:bg-transparent focus:!bg-transparent active:bg-transparent active:!bg-transparent"
            aria-label={sidebarOpen ? "Close menu" : "Open menu"}
            aria-expanded={sidebarOpen}
            onClick={onSidebarToggle}
          >
            <FiMenu className="h-5 w-5" aria-hidden />
          </button>

          <nav
            aria-label="Breadcrumb"
            className="breadcrumbs min-w-0 overflow-x-auto text-sm"
          >
            <ul className="min-h-10 items-center whitespace-nowrap sm:min-h-0">
              {breadcrumbs.map((crumb, index) => {
                const isLast = index === breadcrumbs.length - 1;
                const content =
                  crumb.to && !isLast ? (
                    <Link
                      to={crumb.to}
                      className="text-base-content/70 transition-colors hover:text-primary hover:underline"
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span
                      className={
                        isLast
                          ? "font-semibold text-base-content"
                          : "text-base-content/70"
                      }
                    >
                      {crumb.label}
                    </span>
                  );
                return <li key={`${crumb.label}-${index}`}>{content}</li>;
              })}
            </ul>
          </nav>
        </div>

        <div className="navbar-end shrink-0 gap-1">
          <div className="dropdown dropdown-end">
            <button
              type="button"
              tabIndex={0}
              aria-label="Account menu"
              className="btn btn-ghost btn-sm gap-2 rounded-xl border-0 px-2 normal-case hover:bg-transparent hover:!bg-transparent focus:bg-transparent focus:!bg-transparent active:bg-transparent active:!bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25"
            >
              <div className="avatar placeholder">
                <div className="w-9 rounded-full bg-primary text-xs font-semibold text-primary-content ring-2 ring-base-100 max-sm:ring-0">
                  <span>{initials}</span>
                </div>
              </div>
              <span className="hidden max-w-[10rem] flex-col items-start truncate text-left text-sm lg:flex">
                <span className="truncate font-medium leading-none">
                  {user?.name || "Signed in"}
                </span>
                <span className="mt-0.5 truncate text-xs font-normal text-base-content/60">
                  {user?.email || "—"}
                </span>
              </span>
            </button>

            <ul
              tabIndex={0}
              className="menu dropdown-content menu-sm z-[1] mt-2 w-60 rounded-box border border-base-200 bg-base-100 p-2 shadow-xl [&>li>button:hover]:!bg-transparent"
            >
              <li className="menu-title px-2 py-1 lg:hidden">
                <span className="block truncate text-sm font-semibold normal-case text-base-content">
                  {user?.name || "Signed in"}
                </span>
                <span className="mt-0.5 block truncate text-xs font-normal normal-case text-base-content/60">
                  {user?.email || "—"}
                </span>
              </li>
              <li>
                <button
                  type="button"
                  className="flex w-full cursor-pointer items-center justify-start gap-3 rounded-lg bg-transparent px-3 py-2.5 text-left text-sm font-medium text-error shadow-none ring-0 transition-opacity hover:bg-transparent hover:!bg-transparent hover:opacity-90 focus:bg-transparent focus:!bg-transparent focus:outline-none focus-visible:bg-transparent focus-visible:!bg-transparent active:bg-transparent active:!bg-transparent active:opacity-80 focus-visible:ring-2 focus-visible:ring-error/30 focus-visible:ring-offset-0"
                  onClick={() => dispatch(logoutUser())}
                >
                  <FiLogOut className="h-4 w-4 shrink-0" aria-hidden />
                  Sign out
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </header>
  );
}

export default AppTopbar;
